import {
  ISite,
  setAssumedSrs,
  setGlobalSrs,
} from "../../../apiFacades/csu/site";
import { style } from "typestyle";
import React, { useEffect, useState } from "react";
import { useMatrixState } from "./useMatrixState";
import { Button, ButtonStyleTypes, TextInput } from "@traceair/tds";
import { getEpsgFromSite, getTranslationMatrixFromSite, isEpsg } from "./utils";
import TranslationMatrixInput from "./TranslationMatrixInput";
import { TranslationMatrix } from "../matrixCalculator/types";

interface ISrsConfigurationFormProps {
  customerId: string;
  siteId: string;
  site: ISite;
  // Struggling to come up with correct type. Not that important for now,
  // ideally would be great to drop redux at all.
  dispatch: Function;
}

const headerStyle = style({
  display: "flex",
  marginBottom: "20px",
  marginTop: "10px",
});

const formStyle = style({
  display: "flex",
  justifyContent: "center",
  flexDirection: "column",
  padding: 15,
  backgroundColor: "white",
  boxShadow: "0px 2px 2px rgba(0, 0, 0, 0.24), 0px 0px 2px rgba(0, 0, 0, 0.12)",
  borderRadius: "12px",
});

const SrsConfigurationForm = ({
  customerId,
  siteId,
  site,
  dispatch,
}: ISrsConfigurationFormProps) => {
  const [epsg, setEpsg] = useState<string | undefined>(undefined);
  const matrixState = useMatrixState();
  const { translationMatrix, setTranslationMatrix, isValidMatrixInput } =
    matrixState;
  const [isSaving, setIsSaving] = useState(false);

  // State is needed to validate if the data was saved
  const [previousEpsg, setPreviousEpsg] = useState<string | undefined>(
    getEpsgFromSite(site),
  );
  const [previousTranslationMatrix, setPreviousTranslationMatrix] = useState<
    TranslationMatrix | undefined
  >(getTranslationMatrixFromSite(site));

  const hasDiff = () => {
    const comparableTmPrevious =
      previousTranslationMatrix && previousTranslationMatrix.join("");
    const comparableTmNew = translationMatrix && translationMatrix.join("");
    return previousEpsg !== epsg || comparableTmPrevious !== comparableTmNew;
  };

  useEffect(() => {
    setEpsg(previousEpsg);
    setTranslationMatrix(previousTranslationMatrix);
    // Would cause infinite loop if specify setTranslationMatrix dependency that is not actually needed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previousTranslationMatrix, previousEpsg, site]);

  return (
    <div className={formStyle}>
      <div className={headerStyle}>EPSG Code</div>
      <TextInput
        onChange={setEpsg}
        value={epsg || ""}
        errorMessage="Not a valid EPSG code"
        isInvalid={!isEpsg(epsg || "")}
      />
      <br />
      <div className={headerStyle}>
        <div className={style({ marginRight: "8px" })}>Translation Matrix</div>
        <button
          className={style({ padding: "2px 4px" })}
          onClick={() => {
            setTranslationMatrix(undefined);
          }}
        >
          Reset
        </button>
        <button
          className={style({ padding: "2px 4px", marginLeft: "4px" })}
          onClick={() => {
            navigator.clipboard
              .readText()
              .then((matrixText) => {
                const elements = matrixText
                  .split(/\r\n|\n|\r|\s/g)
                  .map((element) => {
                    const trimmed = element.trim();
                    const parsed = Number(trimmed);
                    if (isNaN(parsed)) {
                      throw Error(
                        `Failed to paste, ${trimmed} is not a number`,
                      );
                    }
                    return parsed;
                  });
                if (elements.length !== 6) {
                  throw Error(
                    `Failed to paste, expected 6 numbers but got ${elements}`,
                  );
                }
                console.log(elements);
                setTranslationMatrix(elements as TranslationMatrix);
              })
              .catch((error) => {
                alert(`Failed to read text from clipboard: ${error}`);
              });
          }}
        >
          Paste
        </button>
      </div>
      <TranslationMatrixInput {...matrixState} />
      <Button
        onClick={() => {
          if (!epsg) {
            return;
          }
          if (translationMatrix) {
            if (!confirm("Are you sure yo want to set assumed CRS?")) {
              return;
            }
            setIsSaving(true);
            dispatch(setAssumedSrs(customerId, siteId, epsg, translationMatrix))
              .then(() => {
                // to reset diff state and de-activate set button
                setPreviousEpsg(epsg);
                setPreviousTranslationMatrix(translationMatrix);
              })
              .catch(() =>
                alert(
                  "Error updating CRS. Probably input is incorrect or service is unavailable",
                ),
              )
              .finally(() => setIsSaving(false));
            return;
          }
          if (!confirm("Are you sure yo want to set global CRS?")) {
            return;
          }
          setIsSaving(true);
          dispatch(setGlobalSrs(customerId, siteId, epsg))
            .then(() => {
              // to reset diff state and de-activate set button
              setPreviousEpsg(epsg);
              setPreviousTranslationMatrix(translationMatrix);
            })
            .catch(() =>
              alert(
                "Error updating CRS. Probably input is incorrect or service is unavailable",
              ),
            )
            .finally(() => setIsSaving(false));
        }}
        pending={isSaving}
        title={`Set ${translationMatrix ? "assumed" : "global"} CRS`}
        disabled={!hasDiff() || !isValidMatrixInput || !isEpsg(epsg || "")}
        styleType={
          previousEpsg ? ButtonStyleTypes.WarnA : ButtonStyleTypes.ActiveA
        }
      />
    </div>
  );
};

export default SrsConfigurationForm;
